/**
 * /hypnose/questions-frequentes — FAQ dédiée, indexable individuellement.
 */
import React from "react";
import { Seo } from "../../components/Layout";
import { PageHero, EndCTA } from "../../components/PageParts";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";

const FAQ = [
  {
    q: "Vais-je perdre le contrôle pendant l'hypnose ?",
    a: [
      "Non, jamais. L'hypnose est un état naturel de conscience modifiée, comparable à ces moments où l'on est absorbé·e dans une lecture ou une rêverie.",
      "Vous entendez ma voix, vous percevez vos ressentis, vous pouvez parler, bouger et ouvrir les yeux à tout moment. Vous restez pleinement acteur·rice de la séance.",
    ],
  },
  {
    q: "Suis-je « hypnotisable » ?",
    a: [
      "Toute personne motivée et disponible peut entrer en état d'hypnose. Il ne s'agit pas d'une performance mais d'une disposition intérieure.",
      "Mon rôle est de créer un cadre sécurisant et de m'adapter à votre fonctionnement — chacun·e vit l'hypnose à sa manière, et tout est juste.",
    ],
  },
  {
    q: "Combien de séances faut-il ?",
    a: [
      "Pour un travail en hypnose ericksonienne (stress, sommeil, confiance…), 2 à 5 séances suffisent souvent.",
      "Pour l'hypnose spirituelle, plusieurs séances sont souvent nécessaires — à raison d'une séance par mois environ — pour laisser au travail intérieur le temps de se déployer. Nous évaluons cela ensemble, sans engagement.",
    ],
  },
  {
    q: "Y a-t-il des contre-indications ?",
    a: [
      "L'hypnose est déconseillée aux personnes souffrant de troubles psychiatriques lourds (schizophrénie, troubles dissociatifs, psychose). En cas de doute, parlez-en avec votre médecin ou votre psychiatre avant de réserver.",
      "Les séances que je propose sont des accompagnements de mieux-être et ne remplacent en aucun cas un suivi médical ou psychologique.",
    ],
  },
  {
    q: "Les séances en ligne sont-elles aussi efficaces qu'en présentiel ?",
    a: [
      "Oui. La grande majorité de mes séances se déroulent en visioconférence, et l'expérience est aussi qualitative qu'en présentiel.",
      "Le confort de votre propre espace facilite souvent le lâcher-prise. Il vous suffit d'un endroit calme, d'écouteurs et d'une connexion stable.",
    ],
  },
  {
    q: "Comment savoir si c'est l'ericksonienne ou la spirituelle qui me convient ?",
    a: [
      "Si vous avez un objectif précis (stress, sommeil, confiance en soi, phobie légère…), l'hypnose ericksonienne est généralement la plus adaptée.",
      "Si vous traversez une période de questionnement, de transition ou d'éveil intérieur, l'hypnose spirituelle sera plus juste. En cas de doute, nous en parlons ensemble avant la séance.",
    ],
  },
  {
    q: "Quels sont les tarifs ?",
    a: [
      "Hypnose ericksonienne : 80 € (environ 1h à 1h15).",
      "Hypnose spirituelle : 120 € (environ 2h).",
      "Séance à distance : règlement à la réservation (virement ou PayPal). Séance en présentiel à Pantin : règlement sur place (espèces ou chèque).",
    ],
  },
  {
    q: "Comment prendre rendez-vous ?",
    a: [
      "Le plus simple est de m'écrire par e-mail ou via le formulaire de la page Réserver. Je vous réponds personnellement sous 24 h.",
    ],
  },
];

export default function HypnoseFAQ() {
  const BREADCRUMB = [
    { to: "/", label: "Accueil" },
    { to: "/hypnose", label: "L'Hypnose" },
    { label: "Questions fréquentes" },
  ];
  return (
    <>
      <Seo
        title="Questions fréquentes sur l'hypnose"
        description="Perte de contrôle, hypnotisabilité, contre-indications, séances en ligne, tarifs, rythme… l'essentiel à savoir avant une première séance d'hypnose avec Valentine DL, à Pantin (Métro Église de Pantin) ou en ligne."
        canonical="https://www.parfaitsimparfaits.fr/hypnose/questions-frequentes"
      />

      <PageHero
        breadcrumb={BREADCRUMB}
        eyebrow="Questions fréquentes"
        title="Vos questions,"
        italic="mes réponses."
        subtitle="L'essentiel à savoir avant de réserver — pour aborder votre première séance en confiance."
      />

      <section className="px-6 lg:px-10 pb-16">
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3" data-testid="hypnose-faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`hypnose-faq-item-${i}`}
                className="border border-sand-300/60 rounded-2xl bg-white/60 px-6 !border-b overflow-hidden"
              >
                <AccordionTrigger className="!text-base sm:!text-lg !font-serif !text-sand-900 !py-6 !no-underline hover:!no-underline text-left">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="!text-base text-sand-700 leading-relaxed pb-6">
                  {item.a.map((p, k) => (
                    <p key={k} className={k > 0 ? "mt-3" : ""}>{p}</p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <EndCTA
        title="Une question qui n'est pas dans la liste ?"
        text="Écrivez-moi, je vous réponds personnellement sous 24 h."
        testid="faq-end-cta"
      />

      {/* Schema.org FAQ pour rich snippet */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map(({ q, a }) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a.join(" ") },
            })),
          }),
        }}
      />
    </>
  );
}
